'use client';

import { useState, useCallback, useEffect, useRef, useMemo, memo } from 'react';
import { MessageSquare, ThumbsUp, Send, User, ChevronDown, ArrowUpDown } from 'lucide-react';
import StarRating, { StarDisplay } from './StarRating';
import type { Review, ReviewStats } from '@/lib/reviews';
import type { Dictionary } from '@/lib/i18n/dictionaries/en';
import { localeTag, type Locale } from '@/lib/i18n/config';
import { trackReviewSubmit } from '@/lib/analytics';

const PAGE_SIZE = 5;

interface ReviewsSectionProps {
  toolSlug: string;
  gradient: string;
  initialStats: ReviewStats;
  initialReviews: Review[];
  initialTotal: number;
  dict: Dictionary;
  locale: string;
}

// ─── Aggregate Header ──────────────────────────────────────────────────────────

function AggregateHeader({ stats, gradient, dict }: { stats: ReviewStats; gradient: string; dict: Dictionary }) {
  // For distribution bars: use real count when available, seed count otherwise
  const barTotal = stats.isSeed ? 0 : stats.ratingCount;
  const pct = (n: number) =>
    barTotal === 0 ? 0 : Math.round((n / barTotal) * 100);

  return (
    <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center">
      <div className="flex flex-col items-center min-w-[90px]">
        <span
          className="font-heading text-5xl font-bold text-transparent bg-clip-text leading-none"
          style={{ backgroundImage: gradient }}
        >
          {stats.ratingValue > 0 ? stats.ratingValue.toFixed(1) : '—'}
        </span>
        <div className="mt-1"><StarDisplay value={stats.ratingValue} size="sm" dict={dict} /></div>
        <span className="text-xs text-textSecondary mt-1.5">
          {stats.ratingCount === 0 && !stats.isSeed
            ? dict.reviews.noReviewsYet
            : `${stats.ratingCount.toLocaleString()} ${stats.ratingCount === 1 ? dict.reviews.reviewWord : dict.reviews.reviewsWord}`}
        </span>
      </div>
      <div className="flex-1 w-full space-y-1.5">
        {([5, 4, 3, 2, 1] as const).map((star) => {
          const count = stats.distribution[star] ?? 0;
          const p = pct(count);
          return (
            <div key={star} className="flex items-center gap-2 text-xs text-textSecondary">
              <span className="w-3 text-end font-medium">{star}</span>
              <div className="flex-1 h-1.5 bg-borderDark rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${p}%`, backgroundImage: gradient }}
                />
              </div>
              <span className="w-5 text-end">{count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Scroll reveal ─────────────────────────────────────────────────────────────
// Watches its own element and flips to visible the moment it scrolls into view,
// then stops observing — each card animates in on its own, one by one.
//
// One IntersectionObserver instance shared across every review card, instead
// of a new instance per card. A single observer with a callback registry
// scales to a review list that's grown across many "Load more" pages with no
// added cost; N separate observer instances (same root, same thresholds)
// each doing their own intersection math is pure duplicated overhead once N
// gets large. Module-scoped so it's created once per page load and reused by
// every ReviewCard mounted anywhere on the page.

const revealObserverEntries = new Map<Element, () => void>();
let revealObserver: IntersectionObserver | null = null;

function getRevealObserver() {
  if (revealObserver) return revealObserver;
  revealObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const onVisible = revealObserverEntries.get(entry.target);
        if (onVisible) {
          onVisible();
          revealObserver!.unobserve(entry.target);
          revealObserverEntries.delete(entry.target);
        }
      }
    },
    { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
  );
  return revealObserver;
}

function useRevealOnScroll<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // If the element is already on-screen when it mounts (e.g. the first
    // page of reviews on initial load), reveal it immediately.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setVisible(true);
      return;
    }

    const observer = getRevealObserver();
    revealObserverEntries.set(el, () => setVisible(true));
    observer.observe(el);

    return () => {
      observer.unobserve(el);
      revealObserverEntries.delete(el);
    };
  }, []);

  return { ref, visible };
}

// ─── Review Card ───────────────────────────────────────────────────────────────

const ReviewCard = memo(function ReviewCard({
  review, locale, dict, delay = 0,
}: { review: Review; locale: string; dict: Dictionary; delay?: number }) {
  const date = useMemo(
    () => new Date(review.createdAt).toLocaleDateString(localeTag[locale as Locale] ?? localeTag.en, {
      year: 'numeric', month: 'short', day: 'numeric',
    }),
    [review.createdAt, locale]
  );
  const { ref, visible } = useRevealOnScroll<HTMLDivElement>();

  // Drop the GPU compositor layer once the reveal animation actually
  // finishes — leaving `will-change` on indefinitely pins every loaded
  // review card in its own layer forever, which adds up as more pages
  // of reviews get loaded.
  const handleAnimationEnd = (e: React.AnimationEvent<HTMLDivElement>) => {
    if (e.animationName === 'review-reveal') {
      e.currentTarget.style.willChange = 'auto';
    }
  };

  return (
    <div
      ref={ref}
      onAnimationEnd={handleAnimationEnd}
      className={`review-reveal ${visible ? 'is-visible' : ''} bg-background border border-borderDark rounded-xl p-4 space-y-2`}
      style={{ '--review-delay': `${delay}ms` } as React.CSSProperties}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-bg2 border border-borderDark flex items-center justify-center">
            <User className="h-3.5 w-3.5 text-textSecondary" />
          </div>
          <span className="text-sm font-semibold text-foreground">{review.name}</span>
        </div>
        <span className="text-xs text-textSecondary">{date}</span>
      </div>
      <StarDisplay value={review.rating} size="sm" dict={dict} />
      {review.comment && (
        <p className="text-sm text-textSecondary leading-relaxed">{review.comment}</p>
      )}
    </div>
  );
});

// ─── Submit Form ───────────────────────────────────────────────────────────────

type FormState = 'idle' | 'submitting' | 'success' | 'error';

function SubmitForm({
  toolSlug, gradient, onSubmitted, dict,
}: { toolSlug: string; gradient: string; onSubmitted: (review: Review) => void; dict: Dictionary }) {
  const [rating, setRating]     = useState(0);
  const [name, setName]         = useState('');
  const [comment, setComment]   = useState('');
  const [state, setState]       = useState<FormState>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async () => {
    if (rating === 0) { setErrorMsg(dict.reviews.ratingRequired); return; }
    if (!name.trim()) { setErrorMsg(dict.reviews.nameRequired); return; }
    setState('submitting');
    setErrorMsg('');
    try {
      const res  = await fetch('/api/reviews', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ toolSlug, name, rating, comment }),
      });
      const data = await res.json();
      if (!res.ok) { setErrorMsg(data.error || dict.common.error); setState('error'); return; }
      setState('success');
      trackReviewSubmit({ tool_slug: toolSlug, rating });
      onSubmitted(data.review as Review);
    } catch {
      setErrorMsg(dict.reviews.networkError);
      setState('error');
    }
  };

  if (state === 'success') {
    return (
      <div className="flex flex-col items-center gap-2 py-6 text-center">
        <div className="w-10 h-10 rounded-full flex items-center justify-center badge-ok">
          <ThumbsUp className="h-5 w-5" />
        </div>
        <p className="text-sm font-semibold text-foreground">{dict.reviews.thankYou}</p>
        <p className="text-xs text-textSecondary">{dict.reviews.feedbackHelps}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-textSecondary uppercase tracking-wider">
          {dict.reviews.yourRating} <span className="text-danger">*</span>
        </label>
        <StarRating value={rating} onChange={setRating} size="lg" dict={dict} />
      </div>
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-textSecondary uppercase tracking-wider">
          {dict.reviews.name} <span className="text-danger">*</span>
        </label>
        <input
          type="text" value={name} onChange={(e) => setName(e.target.value)}
          placeholder={dict.reviews.namePlaceholder} maxLength={60}
          className="w-full bg-background border border-borderDark rounded-lg px-3 py-2 text-sm text-foreground placeholder-textSecondary focus:outline-none focus:border-accent transition-colors"
        />
      </div>
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-textSecondary uppercase tracking-wider">
          {dict.reviews.comment} <span className="text-textSecondary font-normal">{dict.reviews.commentOptional}</span>
        </label>
        <textarea
          value={comment} onChange={(e) => setComment(e.target.value)}
          placeholder={dict.reviews.commentPlaceholder}
          maxLength={600} rows={3}
          className="w-full bg-background border border-borderDark rounded-lg px-3 py-2 text-sm text-foreground placeholder-textSecondary focus:outline-none focus:border-accent transition-colors resize-none"
        />
        <div className="text-end text-xs text-textSecondary">{comment.length}/600</div>
      </div>
      {(state === 'error' || errorMsg) && (
        <p className="text-xs badge-err rounded-lg px-3 py-2">{errorMsg}</p>
      )}
      <button
        onClick={handleSubmit} disabled={state === 'submitting'}
        className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-opacity disabled:opacity-50"
        style={{ backgroundImage: gradient }}
      >
        <Send className="h-4 w-4" />
        {state === 'submitting' ? dict.reviews.submitting : dict.reviews.submit}
      </button>
    </div>
  );
}

// ─── Main Section ──────────────────────────────────────────────────────────────

type SortMode = 'latest' | 'top';

export default function ReviewsSection({
  toolSlug, gradient, initialStats, initialReviews, initialTotal, dict, locale,
}: ReviewsSectionProps) {
  const [reviews, setReviews]   = useState<Review[]>(initialReviews);
  const [stats, setStats]       = useState<ReviewStats>(initialStats);
  const [total, setTotal]       = useState(initialTotal);
  const [hasMore, setHasMore]   = useState(initialTotal > PAGE_SIZE);
  const [sort, setSort]         = useState<SortMode>('latest');
  const [loading, setLoading]   = useState(false);
  const [showForm, setShowForm] = useState(false);

  // Sync accurate total + stats from server after a submission or deletion.
  // If server returns 0 real reviews, client falls back to seed automatically
  // because recalcStatsLocal checks newTotal === 0 and restores initialStats.
  const refreshStats = useCallback(async () => {
    try {
      const res  = await fetch(`/api/reviews?slug=${toolSlug}&page=1&sort=latest`);
      const data = await res.json();
      setTotal(data.total);
      setHasMore(data.hasMore);
      // If all real reviews were deleted, restore seed values immediately
      if (data.total === 0) {
        setStats(initialStats);
      }
    } catch {
      // silently ignore — stats will be corrected on next page load
    }
  }, [toolSlug, initialStats]);

  // For the aggregate bar we update locally from the previous stats + the one
  // new review (accurate enough for immediate feedback; refreshStats() then
  // syncs the true server-side numbers in the background).
  //
  // Previously this rebuilt the whole distribution from `allVisible` — the
  // reviews currently loaded on screen — which undercounts every star bucket
  // whenever the visitor hasn't loaded every page yet (e.g. submits a review
  // after only the first 5 of 40 are loaded, and the bars silently drop to
  // reflect just those 5). Incrementing the one bucket the new rating belongs
  // to, starting from the last known-accurate distribution, keeps every other
  // bucket's count exactly what it was instead of quietly losing data that
  // was never loaded to begin with.
  const recalcStatsLocal = useCallback((newReview: Review, newTotal: number) => {
    if (newTotal === 0) {
      // All real reviews gone — fall back to seed values
      setStats(initialStats);
      return;
    }

    setStats((prev) => {
      // When transitioning from seed to real, start fresh from the first real review
      const prevReal  = prev.isSeed ? 0 : prev.ratingValue;
      const prevCount = prev.isSeed ? 0 : prev.ratingCount;
      const trueAvg   = prevCount > 0
        ? Math.round(((prevReal * prevCount + newReview.rating) / newTotal) * 10) / 10
        : newReview.rating;

      const baseDistribution = prev.isSeed
        ? { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
        : { ...prev.distribution };
      const bucket = newReview.rating as 1 | 2 | 3 | 4 | 5;
      baseDistribution[bucket] = (baseDistribution[bucket] || 0) + 1;

      return {
        ratingValue: trueAvg,
        ratingCount: newTotal,
        isSeed: false,
        distribution: baseDistribution as Record<1 | 2 | 3 | 4 | 5, number>,
      };
    });
  }, [initialStats]);

  const handleSortChange = async (newSort: SortMode) => {
    if (newSort === sort || loading) return;
    setSort(newSort);
    setLoading(true);
    try {
      const res  = await fetch(`/api/reviews?slug=${toolSlug}&page=1&sort=${newSort}`);
      const data = await res.json();
      setReviews(data.reviews);
      setTotal(data.total);
      setHasMore(data.hasMore);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    try {
      // Send every id already on screen instead of a page offset — see the
      // route's excludeIds handling for why: offset paging breaks (dupes or
      // skips) if the sort order shifts under a submission that lands
      // between two "Load More" clicks; excluding what's already loaded
      // doesn't have that failure mode.
      const excludeIds = reviews.map((r) => r.id).join(',');
      const res  = await fetch(
        `/api/reviews?slug=${toolSlug}&sort=${sort}&excludeIds=${encodeURIComponent(excludeIds)}`
      );
      const data = await res.json();
      setReviews((prev) => [...prev, ...data.reviews]);
      setHasMore(data.hasMore);
    } finally {
      setLoading(false);
    }
  };

  const handleNewReview = (review: Review) => {
    const newTotal = total + 1;
    // Insert according to the currently active sort — unconditionally
    // prepending put every new review at position #1 even in 'top' mode,
    // regardless of its rating, contradicting the ranking the visitor
    // selected. 'latest' still prepends (a new review is always the newest
    // by definition); 'top' re-sorts by rating with the same tiebreak the
    // server uses (rating desc, then date desc) so the new review lands
    // where it actually belongs.
    const updated = sort === 'top'
      ? [...reviews, review].sort(
          (a, b) => b.rating - a.rating || new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
      : [review, ...reviews];
    setReviews(updated);
    recalcStatsLocal(review, newTotal);
    refreshStats(); // sync accurate total from server in background
    setShowForm(false);
  };

  const shown = reviews.length;

  return (
    <div className="bg-bg2 border border-borderDark rounded-2xl overflow-hidden">

      {/* Header */}
      <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-borderDark flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 rounded-full" style={{ backgroundImage: gradient }} />
          <h2 className="text-lg font-bold text-foreground font-heading flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-textSecondary" />
            {dict.reviews.heading}
            {total > 0 && (
              <span className="text-xs font-normal text-textSecondary">({total.toLocaleString()})</span>
            )}
          </h2>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="text-xs font-semibold px-4 py-2 rounded-lg border border-borderDark text-foreground hover:border-accent transition-colors"
        >
          {showForm ? `✕ ${dict.reviews.cancel}` : `+ ${dict.reviews.writeReview}`}
        </button>
      </div>

      <div className="p-6 space-y-6">

        {/* Aggregate */}
        <AggregateHeader stats={stats} gradient={gradient} dict={dict} />

        {/* Write form */}
        {showForm && (
          <div className="bg-background border border-borderDark rounded-xl p-5">
            <h3 className="text-sm font-bold text-foreground mb-4">{dict.reviews.writeYourReview}</h3>
            <SubmitForm toolSlug={toolSlug} gradient={gradient} onSubmitted={handleNewReview} dict={dict} />
          </div>
        )}

        {/* Sort + count row — only when reviews exist */}
        {total > 0 && (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-textSecondary">
              {dict.reviews.showingOfTemplate
                .replace('{shown}', String(shown))
                .replace('{total}', total.toLocaleString())}
            </p>
            <div className="flex items-center gap-1 bg-background border border-borderDark rounded-lg p-1">
              <ArrowUpDown className="h-3 w-3 text-textSecondary ms-1" />
              {(['latest', 'top'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => handleSortChange(s)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-colors capitalize ${
                    sort === s ? 'bg-accent text-white' : 'text-textSecondary hover:text-foreground'
                  }`}
                >
                  {s === 'latest' ? dict.reviews.sortLatest : dict.reviews.sortTop}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* List */}
        {reviews.length === 0 ? (
          <div className="text-center py-8 text-textSecondary text-sm">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p>{dict.reviews.noReviewsLong}</p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {reviews.map((r, i) => (
                <ReviewCard
                  key={r.id}
                  review={r}
                  locale={locale}
                  dict={dict}
                  delay={(i % PAGE_SIZE) * 90}
                />
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-borderDark text-sm font-semibold text-textSecondary hover:text-foreground hover:border-accent transition-colors disabled:opacity-50"
              >
                <ChevronDown className={`h-4 w-4 ${loading ? 'animate-bounce' : ''}`} />
                {loading ? dict.reviews.loading : dict.reviews.loadMoreReviewsTemplate.replace('{count}', String(total - shown))}
              </button>
            )}

            {/* All loaded */}
            {!hasMore && total > PAGE_SIZE && (
              <p className="text-center text-xs text-textSecondary py-2">
                ✓ {dict.reviews.allLoadedTemplate.replace('{total}', total.toLocaleString())}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
